import React from 'react';
import { Button, Message, Table, Tooltip, Whisper, useToaster } from 'rsuite';
import { SortType } from 'rsuite-table';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import * as blogCategoriesApi from '../../api/blogCategories';
import PageLayout from '../../common/PageLayout';
import { formatDateTime, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import { useFormErrors } from '../../common/useFormErrors';
import { useConfirm } from '../../common/useConfirm';
import CreateBlogCategoryModal from './CreateBlogCategoryModal';
import EditBlogCategoryModal from './EditBlogCategoryModal';

const { Column, HeaderCell, Cell } = Table;

type BlogCategory = {
  id: string;
  slug: string;
  title: string;
  titleEn?: string | null;
  titleDe?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

const normalizeTimestamp = (value: BlogCategory['createdAt']): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const numeric = Number(value.trim());
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
};

export default function BlogCategories() {
  const [confirm, ConfirmDialog] = useConfirm();
  const toaster = useToaster();
  const [categories, setCategories] = React.useState<BlogCategory[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [modalCategoryId, setModalCategoryId] = React.useState('');
  const [modalSlug, setModalSlug] = React.useState('');
  const [modalTitle, setModalTitle] = React.useState('');
  const [modalTitleEn, setModalTitleEn] = React.useState('');
  const [modalTitleDe, setModalTitleDe] = React.useState('');
  const [sortColumn, setSortColumn] =
    React.useState<keyof BlogCategory>('updatedAt');
  const [sortType, setSortType] = React.useState<SortType>('desc');

  const {
    errorSummary: updateErrors,
    setApiError: setUpdateError,
    clearErrors: clearUpdateErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити категорію. Спробуйте пізніше',
    resetOn: [modalCategoryId],
  });

  const getData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await blogCategoriesApi.list();
      setCategories(response.categories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    getData().catch(console.error);
  }, [getData]);

  const sortedCategories = React.useMemo(() => {
    if (!sortColumn || !sortType) {
      return categories;
    }

    const sorted = [...categories];
    sorted.sort((a, b) => {
      let result = 0;
      if (sortColumn === 'slug') {
        result = a.slug.localeCompare(b.slug, 'uk', { sensitivity: 'base' });
      } else if (sortColumn === 'title') {
        result = a.title.localeCompare(b.title, 'uk', { sensitivity: 'base' });
      } else if (sortColumn === 'createdAt') {
        result =
          normalizeTimestamp(a.createdAt) - normalizeTimestamp(b.createdAt);
      } else if (sortColumn === 'updatedAt') {
        result =
          normalizeTimestamp(a.updatedAt) - normalizeTimestamp(b.updatedAt);
      }

      return sortType === 'asc' ? result : -result;
    });

    return sorted;
  }, [categories, sortColumn, sortType]);

  const handleModalClose = () => {
    setModalCategoryId('');
    setModalSlug('');
    setModalTitle('');
    setModalTitleEn('');
    setModalTitleDe('');
  };

  const setRowDataEdition = (rowData: BlogCategory) => {
    setModalCategoryId(rowData.id);
    setModalSlug(rowData.slug);
    setModalTitle(rowData.title);
    setModalTitleEn(rowData.titleEn || '');
    setModalTitleDe(rowData.titleDe || '');
  };

  const updateCategory = async () => {
    if (!modalCategoryId) {
      return;
    }

    clearUpdateErrors();
    try {
      await blogCategoriesApi.update(
        modalCategoryId,
        modalSlug,
        modalTitle,
        modalTitleEn,
        modalTitleDe
      );
      handleModalClose();
      getData().catch(console.error);
    } catch (err: any) {
      const errorMessage = setUpdateError(err)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    }
  };

  return (
    <PageLayout
      title="Категорії блогу"
      actions={
        <Button appearance="primary" onClick={() => setCreateModalOpen(true)}>
          Створити
        </Button>
      }
    >
      <div className="section-card">
        <Table
          wordWrap="break-word"
          loading={loading}
          autoHeight={true}
          affixHeader={true}
          affixHorizontalScrollbar={true}
          data={sortedCategories}
          renderLoading={renderTableLoading}
          renderEmpty={renderTableEmpty}
          rowClassName={getRowClassName}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={(nextColumn, nextType) => {
            setSortColumn(nextColumn as keyof BlogCategory);
            setSortType(nextType || 'asc');
          }}
        >
          {renderRowNumberColumn()}

          <Column fixed="left" flexGrow={2} minWidth={180} fullText sortable>
            <HeaderCell>Slug</HeaderCell>
            <Cell dataKey="slug" />
          </Column>
          <Column flexGrow={3} minWidth={200} fullText sortable>
            <HeaderCell>Назва</HeaderCell>
            <Cell dataKey="title" />
          </Column>
          <Column flexGrow={1} minWidth={110} sortable>
            <HeaderCell>Створено</HeaderCell>
            <Cell
              dataKey="createdAt"
              renderCell={(content: number) => formatDateTime(content)}
            />
          </Column>
          <Column flexGrow={1} minWidth={110} sortable>
            <HeaderCell>Оновлено</HeaderCell>
            <Cell
              dataKey="updatedAt"
              renderCell={(content: number) => formatDateTime(content)}
            />
          </Column>

          <Column fixed="right" width={100}>
            <HeaderCell>Дії</HeaderCell>
            <Cell style={{ padding: '4px 6px' }}>
              {(rowData: BlogCategory) => (
                <div className="action-icons">
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>Редагувати</Tooltip>}
                  >
                    <Button
                      appearance="link"
                      onClick={() => {
                        setRowDataEdition(rowData);
                      }}
                    >
                      <EditIcon />
                    </Button>
                  </Whisper>
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={<Tooltip>Видалити</Tooltip>}
                  >
                    <Button
                      appearance="subtle"
                      className="delete-btn"
                      onClick={async () => {
                        const result = await confirm({
                          message: `Ви впевнені, що хочете видалити категорію ${rowData.title}?`,
                          destructive: true,
                          confirmText: 'Видалити',
                        });
                        if (!result) {
                          return;
                        }
                        blogCategoriesApi
                          .remove(rowData.id)
                          .catch(console.error)
                          .then(() => getData().catch(console.error));
                      }}
                    >
                      <DeleteIcon />
                    </Button>
                  </Whisper>
                </div>
              )}
            </Cell>
          </Column>
        </Table>
      </div>

      {ConfirmDialog}
      <EditBlogCategoryModal
        open={modalCategoryId.length > 0}
        onClose={handleModalClose}
        slug={modalSlug}
        title={modalTitle}
        titleEn={modalTitleEn}
        titleDe={modalTitleDe}
        setSlug={setModalSlug}
        setTitle={setModalTitle}
        setTitleEn={setModalTitleEn}
        setTitleDe={setModalTitleDe}
        onSave={updateCategory}
        errorSummary={updateErrors}
      />

      <CreateBlogCategoryModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={getData}
      />
    </PageLayout>
  );
}
